import React, { useEffect, useState } from "react";
import { IoCaretDownOutline, IoCaretUpOutline } from "react-icons/io5";
import Nodata from "../NoData/NoData";
// import Pagination from "../../Pagination/Pagination";
import { TableSkeletonLoader } from "../../Loader/SkeletonLoader";
import CustomCheckbox from "../Checkbox/Checkbox";
import { formatLabel } from "../../../utils/formatters";

const getMobileHeading = (tableHeadings, cellIndex, isHeaderCheckbox) =>
  tableHeadings[isHeaderCheckbox ? cellIndex - 1 : cellIndex] || "Select";

const isActionHeading = (heading) =>
  String(heading?.props?.children ?? heading)
    .toLowerCase()
    .includes("action");

const EMPTY_TEXT_VALUES = new Set(["", "NA", "N/A"]);

const isEmptyCellValue = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" &&
    EMPTY_TEXT_VALUES.has(value.trim().toUpperCase()));

const shouldPreserveText = (value) => /@|https?:\/\/|www\./i.test(value);

const formatCellText = (value) =>
  shouldPreserveText(value) ? value : formatLabel(value, "N/A");

const VOID_ELEMENT_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const renderCellValue = (value, nested = false) => {
  if (
    nested &&
    (value === null || value === undefined || typeof value === "boolean")
  ) {
    return null;
  }
  if (nested && typeof value === "string" && value.trim() === "") return null;
  if (isEmptyCellValue(value))
    return <span className="text-gray-400">N/A</span>;
  if (typeof value === "string") return formatCellText(value);
  if (Array.isArray(value))
    return value.map((item) => renderCellValue(item, true));
  if (React.isValidElement(value)) {
    const children = value.props?.children;
    if (VOID_ELEMENT_TAGS.has(value.type)) return value;
    if (children === undefined || children === null) return value;
    if (isEmptyCellValue(children)) {
      return React.cloneElement(value, undefined, "N/A");
    }
    return React.cloneElement(
      value,
      undefined,
      React.Children.map(children, (child) => renderCellValue(child, true)),
    );
  }
  return value;
};

const getHeadingText = (heading) =>
  String(heading?.props?.children ?? heading).toLowerCase();

const getColumnMeta = (heading) => {
  const headingText = getHeadingText(heading);

  if (headingText === "sku") {
    return {
      headerClass: "min-w-[260px] w-[260px]",
      cellClass: "min-w-[260px] w-[260px] whitespace-nowrap",
      contentClass:
        "block max-w-[260px] overflow-hidden text-ellipsis whitespace-nowrap",
    };
  }

  if (headingText.includes("family") || headingText.includes("product")) {
    return {
      headerClass: "min-w-[280px] w-[280px]",
      cellClass: "min-w-[280px] w-[280px] whitespace-nowrap",
      contentClass:
        "block max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap",
    };
  }

  if (headingText.includes("seller")) {
    return {
      headerClass: "min-w-[180px] w-[180px]",
      cellClass: "min-w-[180px] w-[180px] whitespace-nowrap",
      contentClass:
        "block max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap",
    };
  }

  if (
    headingText.includes("stock") ||
    headingText.includes("reserved") ||
    headingText.includes("attributes")
  ) {
    return {
      headerClass: "min-w-[120px] w-[120px]",
      cellClass: "min-w-[120px] w-[120px] whitespace-nowrap",
      contentClass:
        "block max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap",
    };
  }

  if (headingText.includes("description")) {
    return {
      headerClass: "min-w-[360px] max-w-[420px] w-[380px]",
      cellClass:
        "admin-table-description-cell min-w-[360px] max-w-[420px] w-[380px]",
      contentClass:
        "block max-w-[380px] overflow-hidden text-ellipsis whitespace-nowrap",
    };
  }

  if (headingText.includes("status")) {
    return {
      headerClass: "min-w-[120px] w-[120px] text-center",
      cellClass: "min-w-[120px] w-[120px] text-center",
      contentClass: "flex justify-center",
    };
  }

  if (headingText.includes("action")) {
    return {
      headerClass: "min-w-[140px] text-center",
      cellClass: "admin-table-action-cell min-w-[140px] text-center",
      contentClass: "flex justify-center",
    };
  }

  return {
    headerClass: "min-w-[160px] w-[160px]",
    cellClass: "min-w-[160px] w-[160px] whitespace-nowrap",
    contentClass:
      "block max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap",
  };
};

const TotalRecordsSummary = ({ total }) => (
  <div className="flex items-center justify-between border-t border-[var(--admin-line)] bg-white px-5 py-3 text-sm text-[var(--admin-muted)]">
    <span>Total Records</span>
    <span className="font-semibold text-gray-700">{total}</span>
  </div>
);

const TableData = ({
  tableHeadings = [],
  isHeaderCheckbox = false,
  showExtraHeading,
  data,
  sortColumn,
  sortDirection,
  handleSort,
  showTableSorting = true,
  totalData,
  totalSize,
  currentPage,
  pageSize,
  onPageChange,
  loading = false,
  sortableColumns = [],
  allRowsSelected,
  handleHeaderCheckboxChange,
  error = "",
  emptyMessage = "No Data found",
  rowKey,
  stickyHeader = true,
  actions,
  onRefresh,
  showSerialNumber = true,
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  const hasSerialHeading = tableHeadings.some((heading) => {
    const value = String(heading?.props?.children ?? heading)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    return ["sno", "srno", "serial", "serialnumber"].includes(value);
  });
  const shouldShowSerialNumber = showSerialNumber && !hasSerialHeading;
  const resolvedCurrentPage = Number(currentPage || 1);
  const resolvedPageSize = Number(pageSize || totalSize || data?.length || 10);
  const visibleColumnCount =
    tableHeadings.length +
    (shouldShowSerialNumber ? 1 : 0) +
    (isHeaderCheckbox ? 1 : 0) +
    (showExtraHeading ? Math.max(0, 10 - tableHeadings.length) : 0);
  const totalRecords = totalData ?? data?.length ?? 0;
  const showLoading =
    loading ||
    (!hasMounted && (!Array.isArray(data) || data.length === 0) && !error);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <>
      <section className="admin-card w-full overflow-hidden bg-white">
        {(actions || onRefresh) && (
          <div className="admin-table-toolbar flex-wrap">
            <div className="flex flex-wrap  items-center gap-2">{actions}</div>
            {onRefresh && (
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={onRefresh}
              >
                Refresh
              </button>
            )}
          </div>
        )}
        <div className="w-full">
          {showLoading ? (
            <TableSkeletonLoader
              columns={visibleColumnCount}
              rows={totalSize || 10}
            />
          ) : error ? (
            <div className="admin-table-error" role="alert">
              {error}
            </div>
          ) : data && Array.isArray(data) && data.length > 0 ? (
            <>
              <div className="hidden w-full lg:block overflow-x-auto">
                <table className="w-full min-w-max table-auto border-collapse text-left font-inter">
                  <thead
                    className={`admin-table-head text-[13px] ${stickyHeader ? "sticky top-0 z-10 shadow-sm" : ""}`}
                  >
                    <tr>
                      {isHeaderCheckbox && (
                        <th
                          key="checkbox-header"
                          className="w-12 px-4 py-3 text-left align-middle"
                        >
                          <CustomCheckbox
                            checked={allRowsSelected}
                            onChange={handleHeaderCheckboxChange}
                          />
                        </th>
                      )}
                      {shouldShowSerialNumber && (
                        <th className="w-16 px-4 py-3 text-left font-semibold text-[var(--admin-navy)] whitespace-nowrap">
                          S.No
                        </th>
                      )}
                      {tableHeadings.map((heading, index) => {
                        const columnMeta = getColumnMeta(heading);
                        const isActions = isActionHeading(heading);

                        return (
                          <th
                            key={`heading-${index}`}
                            className={`px-5 py-3 text-left font-semibold text-[var(--admin-navy)] whitespace-nowrap select-none ${
                              columnMeta.headerClass
                            } ${
                              sortableColumns.includes(index)
                                ? "cursor-pointer hover:bg-[var(--admin-blue-soft)]"
                                : "cursor-default"
                            }`}
                            onClick={
                              sortableColumns.includes(index)
                                ? () => handleSort && handleSort(index)
                                : undefined
                            }
                          >
                            <div
                              className={`flex items-center gap-2 ${
                                isActions ? "justify-center" : ""
                              }`}
                            >
                              <span>{heading}</span>
                              {sortableColumns.includes(index) &&
                                showTableSorting && (
                                  <div className="flex flex-col text-[var(--admin-muted)]">
                                    <IoCaretUpOutline
                                      size={8}
                                      className={`${
                                        sortColumn === index &&
                                        sortDirection === "asc"
                                          ? "text-[var(--admin-gold)]"
                                          : ""
                                      }`}
                                    />
                                    <IoCaretDownOutline
                                      size={8}
                                      className={`${
                                        sortColumn === index &&
                                        sortDirection === "desc"
                                          ? "text-[var(--admin-gold)]"
                                          : ""
                                      }`}
                                    />
                                  </div>
                                )}
                            </div>
                          </th>
                        );
                      })}
                      {showExtraHeading &&
                        [...Array(Math.max(0, 10 - tableHeadings.length))].map(
                          (_, index) => (
                            <th
                              key={`extra-${index}`}
                              className="px-4 py-3"
                            ></th>
                          ),
                        )}
                    </tr>
                  </thead>
                  <tbody className="bg-white text-[12px] text-slate-600">
                    {data.map((row, rowIndex) => (
                      <tr
                        key={
                          typeof rowKey === "function"
                            ? rowKey(row, rowIndex)
                            : `row-${rowIndex}`
                        }
                        className="border-b border-[#f0e8dc] last:border-0 transition-colors hover:bg-[var(--admin-surface-soft)]"
                      >
                        {isHeaderCheckbox &&
                          row &&
                          Array.isArray(row) &&
                          row.length > 0 && (
                            <td className="w-12 px-4 py-3 align-middle">
                              <div className="flex justify-start">{row[0]}</div>
                            </td>
                          )}
                        {shouldShowSerialNumber && (
                          <td className="w-16 px-4 py-3 align-middle font-medium text-[var(--admin-muted)]">
                            {(resolvedCurrentPage - 1) * resolvedPageSize +
                              rowIndex +
                              1}
                            .
                          </td>
                        )}
                        {row && Array.isArray(row) && row.length > 0 ? (
                          row.map((cell, cellIndex) => {
                            const headingIndex = isHeaderCheckbox
                              ? cellIndex - 1
                              : cellIndex;
                            const isCheckboxCell =
                              isHeaderCheckbox && cellIndex === 0;
                            const isActions = isActionHeading(
                              tableHeadings[headingIndex],
                            );
                            const columnMeta = getColumnMeta(
                              tableHeadings[headingIndex],
                            );

                            if (isCheckboxCell) {
                              return null;
                            }

                            return (
                              <td
                                key={`cell-${rowIndex}-${cellIndex}`}
                                className={`px-5 py-3 align-middle text-[var(--admin-ink)] ${
                                  columnMeta.cellClass
                                }`}
                              >
                                <div
                                  className={`min-w-0 ${
                                    isActions
                                      ? "flex justify-center"
                                      : columnMeta.contentClass
                                  }`}
                                >
                                  {renderCellValue(cell)}
                                </div>
                              </td>
                            );
                          })
                        ) : (
                          <td
                            colSpan={visibleColumnCount}
                            className="px-4 py-8 text-center text-sm text-gray-500"
                          >
                            No Data Available
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="hidden lg:block">
                <TotalRecordsSummary total={totalRecords} />
              </div>

              <div className="space-y-3 p-3 lg:hidden">
                {data.map((row, rowIndex) => (
                  <div
                    key={`mobile-row-${rowIndex}`}
                    className="overflow-hidden rounded-lg border border-[var(--admin-line)] bg-white"
                  >
                    <div className="flex items-center justify-between border-b border-[var(--admin-line)] bg-[var(--admin-table-head)] px-4 py-3">
                      <span className="text-xs font-semibold text-[var(--admin-navy)]">
                        Record{" "}
                        {(resolvedCurrentPage - 1) * resolvedPageSize +
                          rowIndex +
                          1}
                      </span>
                    </div>
                    {row && Array.isArray(row) && row.length > 0 ? (
                      row.map((cell, cellIndex) => (
                        <div
                          key={`mobile-cell-${rowIndex}-${cellIndex}`}
                          className="grid grid-cols-[minmax(96px,42%)_1fr] gap-3 border-b border-[#f0e8dc] px-4 py-3 last:border-b-0"
                        >
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {getMobileHeading(
                              tableHeadings,
                              cellIndex,
                              isHeaderCheckbox,
                            )}
                          </span>
                          <span className="min-w-0 text-right text-sm font-medium text-gray-800">
                            {renderCellValue(cell)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No Data Available
                      </div>
                    )}
                  </div>
                ))}
                <div className="overflow-hidden rounded-lg border border-[var(--admin-line)]">
                  <TotalRecordsSummary total={totalRecords} />
                </div>
              </div>
            </>
          ) : (
            <div className="mt-2">
              <Nodata
                nodataLabel={emptyMessage}
                nodataDesc={"There are no Data to display"}
              />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default TableData;
